import { range } from 'lodash';
import moment from 'moment';
import api from '@/services/api';
import { updateObjectValues } from '@/utils/helpers';
import { ACCOUNT_FUNDS_MAP } from '@/services/notifications/proxy';

const ITEMS_PER_HISTORY_PAGE = 10;
const DEFAULT_HISTORY = {
  data: [],
  page: 0,
  totalCount: -1,
  canLoadMore: true,
  marketId: -1,
};
const DEFAULT_PRICE_CHANGE = 10;

export const ALL_TYPES_OPTION = { label: 'All Transactions', value: 'ALL' };
export const ALL_YEARS_OPTION = { label: 'All', value: '' };

const TYPE_OPTIONS = [
  ALL_TYPES_OPTION,
  { label: 'Deposits', value: 'DEPOSITS' },
  { label: 'Withdrawals', value: 'WITHDRAWALS' },
  { label: 'Promotions', value: 'PROMOTIONS' },
];

function makeYearsOptions(obj) {
  const years = [...obj.keys()].sort().reduceRight((filter, year) => {
    filter.push({ label: year, value: year.toString() });
    return filter;
  }, []);
  years.unshift(ALL_YEARS_OPTION);
  return years;
}

export default {
  namespaced: true,
  state: {
    portfolioChart: {
      history: [],
    },
    userAffiliations: [],
    profileStatus: {},
    profileDetail: {},
    tradeHistory: Object.create(DEFAULT_HISTORY),
    transactionHistory: Object.create(DEFAULT_HISTORY),
    marketHistory: Object.create(DEFAULT_HISTORY),
    pendingHistory: [],
    marketPayouts: null,
    tradedMarkets: [],
    tradedMarketsLoaded: false,
    watchToggle: false,
    isUCAffilSet: false,
    settings: {
      display: {},
      privacy: {},
      InAppNotifications: {},
      PushNotifications: {},
    },
  },
  mutations: {
    resetTransactionHistory(state) {
      state.transactionHistory = Object.create(DEFAULT_HISTORY);
    },
    resetTradeHistory(state) {
      state.tradeHistory = Object.create(DEFAULT_HISTORY);
    },
    resetMarketHistory(state) {
      state.marketHistory = Object.create(DEFAULT_HISTORY);
    },
    updateTransactionHistory(state, payload) {
      const data =
        state.transactionHistory.data.concat(payload.data.fundsHistoryViewModel);
      state.pendingHistory = payload.data.fundsPendingViewModel;
      const page = payload.page;
      const totalCount = payload.data.totalItems;
      const canLoadMore = totalCount > data.length;

      state.transactionHistory = {
        ...state.transactionHistory,
        data,
        page,
        totalCount,
        canLoadMore,
      };
    },
    updateTradeHistory(state, payload) {
      const current = state[payload.what];
      let history;

      if (
        (current.marketId !== payload.marketId) ||
        (payload.contractId !== current.contractId)
      ) {
        history = Object.create(DEFAULT_HISTORY);
      } else {
        history = current;
      }
      const data = history.data.concat(payload.data.historyList);
      const page = payload.page;
      const marketId = payload.marketId;
      const contractId = payload.contractId;
      const totalCount = payload.data.totalCount;
      const canLoadMore = totalCount > data.length;

      state[payload.what] = {
        ...state[payload.what],
        data,
        page,
        marketId,
        contractId,
        totalCount,
        canLoadMore,
      };
    },
    updateMarketPayouts(state, payload) {
      state.marketPayouts = payload;
    },
    setProfileDetail(state, data) {
      state.profileDetail = data;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        gtmUserID: data.userGuid,
        otmUserID: data.otmUserId,
      });
    },
    setProfileStatus(state, data) {
      state.profileStatus = data;
    },
    updateTradedMarkets(state, payload) {
      state.tradedMarkets = payload;
      state.tradedMarketsLoaded = true;
    },
    clearTradedMarkets(state) {
      state.tradedMarkets = [];
      state.tradedMarketsLoaded = false;
    },
    updateTransactionFilters(state, payload) {
      state.transactionFilters = payload;
    },
    setPortfolioChart(state, data) {
      state.portfolioChart = data;
    },
    updateSettingsCategory(state, payload) {
      const categories = payload.reduce((acc, { category, keyName, value }) => {
        if (acc[category] === undefined) {
          acc[category] = {};
        }
        // converting to boolean if true/false
        let boolValue = null;
        if (value === 'true') {
          boolValue = true;
        }
        if (value === 'false') {
          boolValue = false;
        }
        let parsed = value;
        if (keyName === 'ContractPriceChangeAmount') {
          parsed = Number.parseInt(value, 10);
          if (Number.isNaN(parsed)) {
            parsed = DEFAULT_PRICE_CHANGE;
          }
        }
        acc[category][keyName] = boolValue !== null ? boolValue : parsed;
        return acc;
      }, {});
      Object.keys(categories).forEach((k) => {
        state.settings[k] = categories[k];
      });
    },
    updateSettings(state, { category, key, value }) {
      state.settings[category][key] = value;
    },
    toggleWatch(state) {
      state.watchToggle = !state.watchToggle;
    },
    updateProfileFromNotification(state, data) {
      state.profileDetail = updateObjectValues(state.profileDetail, data, ACCOUNT_FUNDS_MAP);
    },
    setUserAffiliations(state, data) {
      state.userAffiliations = data;
    },
    setIsUCAffilSet(state, data) {
      state.isUCAffilSet = data;
    },
  },
  getters: {
    transactionFilters(state) {
      if (state.transactionHistory.data) {
        const registrationYear = moment.utc(state.profileDetail.dateRegistered).year();
        const currentYear = (new Date()).getFullYear();
        const yearPairs = range(registrationYear, currentYear + 1).map(i => [i, i]);
        const yearsMap = new Map(yearPairs);
        const yearOptions = makeYearsOptions(yearsMap);
        return { yearOptions, typeOptions: TYPE_OPTIONS };
      }
      return { yearOptions: [], typeOptions: TYPE_OPTIONS };
    },
    showBidAsk(state) {
      return state.settings.display.ShowBidAsk;
    },
    settingsReady(state) {
      return Object.keys(state.settings.privacy).length > 0 &&
        Object.keys(state.settings.InAppNotifications).length > 0 &&
        Object.keys(state.settings.PushNotifications).length > 0;
    },
  },
  actions: {
    getTradeHistory({ commit, state }, { marketId = '', contractId = '' } = {}) {
      const what = marketId === '' ? 'tradeHistory' : 'marketHistory';
      const history = state[what];
      const force = (history.marketId !== marketId) || (contractId !== history.contractId);

      if (force || history.canLoadMore) {
        const page = force ? 1 : (history.page + 1);
        return api.profile.getTradeHistory(ITEMS_PER_HISTORY_PAGE, page, marketId, contractId)
          .then((response) => {
            commit('updateTradeHistory', {
              page,
              data: response.data,
              marketId,
              contractId,
              what,
            });
          });
      }
      return null;
    },
    getTransactionHistory({ state, commit }, { type = 'ALL', year = '' }) {
      if (state.transactionHistory.canLoadMore) {
        const page = state.transactionHistory.page + 1;
        return api.profile.getTransactionHistory(ITEMS_PER_HISTORY_PAGE, page, type, year)
          .then((response) => {
            commit('updateTransactionHistory', {
              page,
              data: response.data,
            });
            return response.data;
          });
      }
      return null;
    },
    clearMarketPayouts({ commit }) {
      commit('updateMarketPayouts', null);
    },
    getMarketPayouts({ commit }, marketId) {
      return api.profile.getMarketPayouts(marketId).then((response) => {
        commit('updateMarketPayouts', response.data);
      });
    },
    getProfileDetail({ commit }) {
      return api.profile.getProfileDetail()
        .then((response) => {
          commit('setProfileDetail', response.data);
          return response.data;
        });
    },
    getTradedMarkets({ commit, state }, { cache = false }) {
      if (cache && state.tradedMarketsLoaded) {
        return Promise.resolve(state.tradedMarkets);
      }
      return api.profile.getTradeHistoryMarkets()
        .then((response) => {
          commit('updateTradedMarkets', response.data);
          return response.data;
        });
    },
    resetTransactionHistory({ commit }) {
      commit('resetTransactionHistory');
    },
    resetTradeHistory({ commit }) {
      commit('resetTradeHistory');
    },
    resetMarketHistory({ commit }) {
      commit('resetMarketHistory');
    },
    addMarketToWatchList({ commit }, marketId) {
      return api.profile.putMarketWatch(marketId)
        .then((response) => {
          commit('toggleWatch');
          return response.data;
        });
    },
    removeMarketFromWatchList({ commit }, marketId) {
      return api.profile.deleteMarketWatch(marketId)
        .then((response) => {
          commit('toggleWatch');
          return response.data;
        });
    },
    getPortfolioChart({ commit }, { timespan }) {
      return api.profile.getPortfolioChart(timespan)
        .then((response) => {
          commit('setPortfolioChart', response.data);
          return response.data;
        });
    },
    updateSettings({ commit }, payload) {
      return api.profile.putSettings(payload.category, payload.key, payload.value)
        .then((response) => {
          commit('updateSettings', payload);
          return response.data;
        });
    },
    getAllSettings({ commit }) {
      const categories = (['Display', 'InAppNotifications', 'PushNotifications', 'Privacy'])
        .map(i => i.toLowerCase())
        .join();
      return api.profile.getAllSettings(categories)
      .then((response) => {
        commit('updateSettingsCategory', response.data);
        return response.data;
      });
    },
    updateProfileFromNotification({ commit }, payload) {
      commit('updateProfileFromNotification', payload);
    },
    clearTradedMarkets({ commit }) {
      commit('clearTradedMarkets');
    },
    setProfileStatus({ commit }, data) {
      commit('setProfileStatus', data);
    },
    sendInviteEmail({ commit }, email) {
      return api.profile.postSendInviteEmail(email)
      .then(response => response.data);
    },
    getUserAffiliations({ commit }) {
      return api.profile.getUserAffiliations()
        .then((response) => {
          const data = response.data;
          commit('setIsUCAffilSet', data.isUCAffilSet);
          commit('setUserAffiliations', data.affiliateEntities || []);
          return response.data;
        });
    },
    setUserAffiliations({ commit }, payload) {
      return api.profile.setUserAffiliations(payload)
        .then(response => response.data);
    },
    removeUserAffiliations({ commit }, payload) {
      return api.profile.removeUserAffiliations(payload)
        .then(response => response.data);
    },
    getAffiliations({ _ }, search) {
      return api.profile.getAffiliations(search)
        .then(response => response.data);
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/profile.js